/**
 * Boot-time auth hygiene.
 *
 * The app uses a custom session-token system (user_auth_token / admin_auth_token).
 * Supabase JS is configured with `persistSession: true` + `autoRefreshToken: true`,
 * so a previous Google/Apple OAuth sign-in leaves a `sb-*-auth-token` in
 * localStorage that Supabase will silently refresh on tab focus and emit
 * SIGNED_IN events for. That can:
 *   - re-bridge an OLD OAuth identity into a fresh custom session while the
 *     user is mid-way through an email-OTP flow for a different account, and
 *   - flip the app between "signed in" / "signed out" on focus changes.
 *
 * Fix: on app boot, if there is NO custom session but there IS a stale
 * Supabase session, sign Supabase out exactly once. After this, the
 * Supabase client is quiet until the user explicitly clicks Continue with
 * Google/Apple from /login.
 */
import { supabase } from '@/integrations/supabase/client';

let didRun = false;

export async function flushStaleSupabaseSessionOnBoot(): Promise<void> {
  if (didRun) return;
  didRun = true;

  try {
    const hasCustomSession =
      !!localStorage.getItem('user_auth_token') ||
      !!localStorage.getItem('admin_auth_token');
    if (hasCustomSession) return;

    const { data } = await supabase.auth.getSession();
    if (data?.session) {
      // eslint-disable-next-line no-console
      console.info('[authBoot] Flushing stale Supabase session (no custom session present)');
      try { await supabase.auth.signOut(); } catch {}
      // Defensive: nuke any sb-* keys that didn't get cleared.
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && (k.startsWith('sb-') || k.includes('-auth-token'))) {
            localStorage.removeItem(k);
          }
        }
      } catch {}
    }
  } catch {
    // Best-effort. Never block app boot on this.
  }
}
